/* eslint-disable formatjs/no-literal-string-in-jsx */
import Heading from '@atlaskit/heading';
import PageHeader from '@atlaskit/page-header';
import { token } from '@atlaskit/tokens';
import FieldNameContainer from '@databases/field-name/FieldNameContainer';
import {
  FieldNameInlineContainer,
  FieldNameWithActionsContainer,
} from '@databases/field-name/FieldNameWithActions';
import { SkeletonContext, SkeletonItem } from '@databases/skeleton';
import { DatabaseValueViewContainer } from '@databases/styles';
import { StyledInlineEditWrapper } from '@databases/value/DatabaseValueInlineEdit';
import { range } from 'lodash-es';
import React, { type FunctionComponent } from 'react';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { Container as DatabaseEntryContainer, Table } from './DatabaseEntry';

export const DatabaseEntryMacroContainer = styled.div``;

export const ToolbarSkeleton = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: ${token('space.100')};
`;

const DatabaseEntrySkeletonWrapper = styled(DatabaseEntryMacroContainer)`
  * {
    user-select: none;
    border: none;
    outline: none;
    cursor: default;
  }

  ${Table} {
    grid-template-columns: 260px 1fr;
  }

  .add-field {
    grid-column: 1 / -1;
    padding: ${token('space.100')} ${token('space.100', '8px')};
  }
`;

interface DatabaseEntrySkeletonProps {
  showBreadCrumbs?: boolean;
}

const DatabaseEntrySkeleton: FunctionComponent<DatabaseEntrySkeletonProps> = ({
  showBreadCrumbs = false,
}) => {
  const visualRefreshIcons = isFeatureFlagEnabled('platform-visual-refresh-icons');
  return (
    <SkeletonContext isEnabled>
      <DatabaseEntrySkeletonWrapper>
        {showBreadCrumbs ? (
          <>
            <PageHeader>
              <div style={{ height: 4 }} />
              <div style={{ display: 'flex' }}>
                <SkeletonItem>New Entry Name</SkeletonItem>
                <div style={{ flexGrow: 1 }} />
                <SkeletonItem isButton width={30} height={30} style={{ height: 40 }}>
                  More
                </SkeletonItem>
              </div>
            </PageHeader>
          </>
        ) : (
          <ToolbarSkeleton>
            <Heading size="large">
              <SkeletonItem>New Database Name</SkeletonItem>
            </Heading>
            <div style={{ flexGrow: 1 }} />
            <SkeletonItem isButton width={30} height={30} style={{ height: 40 }}>
              More
            </SkeletonItem>
          </ToolbarSkeleton>
        )}

        <DatabaseEntryContainer>
          <Table>
            <tbody>
              {range(5).map((i) => {
                const fieldName = i % 2 === 1 ? 'Long field name' : 'Short field';
                const databaseValue =
                  i % 2 === 0
                    ? 'Very very very very very long value'
                    : 'Shorter entry value';
                return (
                  <tr key={i}>
                    <th>
                      <FieldNameWithActionsContainer>
                        <FieldNameInlineContainer visualRefreshIcons={visualRefreshIcons}>
                          <FieldNameContainer>
                            <SkeletonItem hasIcon>{fieldName}</SkeletonItem>
                          </FieldNameContainer>
                        </FieldNameInlineContainer>
                      </FieldNameWithActionsContainer>
                    </th>
                    <td>
                      <StyledInlineEditWrapper>
                        <DatabaseValueViewContainer>
                          <SkeletonItem>{databaseValue}</SkeletonItem>
                        </DatabaseValueViewContainer>
                      </StyledInlineEditWrapper>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="add-field">
                  <SkeletonItem hasIcon maxWidth={80}>
                    Add field
                  </SkeletonItem>
                </td>
              </tr>
            </tfoot>
          </Table>
        </DatabaseEntryContainer>
      </DatabaseEntrySkeletonWrapper>
    </SkeletonContext>
  );
};

export default DatabaseEntrySkeleton;
